import { createContext, ReactNode, useState, useContext, useEffect } from 'react';
import { Device, DeviceGroup, Loadable, ReadoutPlan, Appliance } from '../types';
import { useDeviceGroups, useReadoutPlans, useAppliances, useSaveDevice } from '../api';
import { AuthContext } from './AuthContext';

interface DeviceContextType {
  device?: Device;
  deviceGroups: Loadable<DeviceGroup>;
  appliances?: Loadable<Appliance>;
  readoutPlans?: Loadable<ReadoutPlan> | undefined;
  setDevice: (device: Device | undefined) => void;
  saveDevice: (device: Device) => Promise<Device>;
}

const loadableGroup: Loadable<DeviceGroup> = { loading: true };
const loadableReadoutPlans: Loadable<ReadoutPlan> = { loading: true };
const loadableAppliances: Loadable<Appliance> = { loading: true };

const initialContext: DeviceContextType = {
  deviceGroups: loadableGroup,
  readoutPlans: loadableReadoutPlans,
  appliances: loadableAppliances,
  setDevice: () => {},
  saveDevice: () => Promise.resolve({} as Device),
};

const DeviceContext = createContext<DeviceContextType>(initialContext);

function DeviceProvider({ children }: { children: ReactNode }) {
  const { auth } = useContext(AuthContext);
  const [device, setDevice] = useState<Device | undefined>();

  // Use React Query hooks for data fetching
  const deviceGroupsQuery = useDeviceGroups();
  const readoutPlansQuery = useReadoutPlans(device?.id);
  const appliancesQuery = useAppliances(device?.id);
  const saveDeviceMutation = useSaveDevice();

  const { refetch } = useDeviceGroups();
  useEffect(() => {
    if (auth && auth.token) {
      // Wait a short time to ensure a token is properly set in apiClient
      setTimeout(() => {
        refetch().then(() => {});
      }, 500);
    }
  }, [auth, refetch]);

  // Convert React Query results to Loadable format for backward compatibility
  const deviceGroups: Loadable<DeviceGroup> = {
    loading: deviceGroupsQuery.isLoading,
    data: deviceGroupsQuery.data,
    error: deviceGroupsQuery.error as Error,
  };

  const readoutPlans: Loadable<ReadoutPlan> = {
    loading: readoutPlansQuery.isLoading,
    data: readoutPlansQuery.data,
    error: readoutPlansQuery.error as Error,
  };

  const appliances: Loadable<Appliance> = {
    loading: appliancesQuery.isLoading,
    data: appliancesQuery.data,
    error: appliancesQuery.error as Error,
  };

  // Use the mutation from React Query for saving devices
  const saveDevice = async (deviceToSave: Device) => {
    try {
      const savedDevice = await saveDeviceMutation.mutateAsync(deviceToSave);
      if (device?.id === savedDevice.id) {
        setDevice(savedDevice);
      }
      return savedDevice;
    } catch (error) {
      throw error;
    }
  };

  return (
    <DeviceContext.Provider
      value={{
        device,
        deviceGroups,
        readoutPlans,
        appliances,
        setDevice,
        saveDevice,
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
}

export { DeviceContext, DeviceProvider };
